import { TenantData } from 'models/tenants';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAppStore } from 'hooks/state/useAppStore';
import { tbPalette } from 'styles/colors';
import type { CSSProperties } from 'react';
import { Select } from 'components/select/Select';
import { useSelectClose, useSelectOpen } from 'components/select/SelectContext';
import type { SelectOption } from 'components/select/types';
import { TbIcon } from 'components/Figma';

interface TenantSelectProps {
  selectedTenant: string | null;
  allTenants: TenantData[] | undefined;
}

const mapTenantToTenantOption = (tenant: TenantData): SelectOption<string> => ({
  id: tenant.accountNumber,
  value: tenant.accountNumber,
  label: `${tenant.accountNumber} - ${tenant.name}`,
});

function TenantOptions({
  options,
  selectedTenant,
  onChange,
}: {
  options: SelectOption<string>[];
  selectedTenant: string | null;
  onChange: (value: string) => void;
}) {
  const close = useSelectClose();
  const open = useSelectOpen();
  const wasOpen = useRef(false);
  const [orderedOptions, setOrderedOptions] = useState(options);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setOrderedOptions(
        [...options].sort(
          (left, right) => Number(right.value === selectedTenant) - Number(left.value === selectedTenant)
        )
      );
    }
    wasOpen.current = open;
  }, [open, options, selectedTenant]);

  useEffect(() => {
    setOrderedOptions(current => {
      const available = new Map(options.map(option => [option.value, option]));
      const ordered = current
        .map(option => available.get(option.value))
        .filter((option): option is SelectOption<string> => option !== undefined);
      const currentValues = new Set(ordered.map(option => option.value));

      return [...ordered, ...options.filter(option => !currentValues.has(option.value))];
    });
  }, [options]);

  return (
    <div className="max-h-[350px] overflow-y-auto py-1">
      {orderedOptions.map(option => {
        if (option.value === undefined) return null;

        const value = option.value;
        const selected = value === selectedTenant;

        return (
          <button
            key={option.value}
            type="button"
            data-testid={`tenant-select-option-${value}`}
            className={
              selected
                ? 'flex h-12 min-h-12 w-full items-center justify-between gap-2 bg-snow-200 px-3 py-4 text-left text-sm hover:bg-snow-200'
                : 'flex h-12 min-h-12 w-full items-center justify-between gap-2 px-3 py-4 text-left text-sm hover:bg-snow-100'
            }
            onClick={() => {
              onChange(value);
              close?.();
            }}
          >
            <span className="min-w-0 flex-1 truncate whitespace-nowrap" title={option.label}>
              {option.label}
            </span>
            {selected && <TbIcon name="Checkmark" size="lg" />}
          </button>
        );
      })}
    </div>
  );
}

export function TenantSelect({ selectedTenant, allTenants }: TenantSelectProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const appStore = useAppStore();
  const allTenantOptions = useMemo(() => allTenants?.map(mapTenantToTenantOption) ?? [], [allTenants]);
  const [searchValue, setSearchValue] = useState('');
  const filteredTenantOptions = useMemo(() => {
    const searchTerm = searchValue.trim().toLowerCase();
    return searchTerm
      ? allTenantOptions.filter(option => option.label?.toLowerCase().includes(searchTerm))
      : allTenantOptions;
  }, [allTenantOptions, searchValue]);
  const onChange = (value: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('accountNumber', value);
    navigate(`${location.pathname}?${searchParams.toString()}`);
    appStore.clearAll();
  };

  const dropDownFill = tbPalette.background.default;

  return (
    <div
      data-testid="tenant-select"
      style={{ backgroundColor: dropDownFill, '--nav-bg': dropDownFill } as CSSProperties}
    >
      <Select
        label="Tenant"
        showLabel={false}
        value={allTenantOptions.find(option => option.value === selectedTenant)?.label ?? ''}
        hasValue={Boolean(selectedTenant)}
        id="tenant-select"
        border="transparent"
        className="bg-[var(--nav-bg)] [&_.select-trigger-content>span]:font-normal [&_legend]:bg-[var(--nav-bg)]"
        fullWidth
        dropdownMinWidth={240}
        onOpen={() => setSearchValue('')}
        search={{
          value: searchValue,
          onChange: setSearchValue,
          placeholder: intl.formatMessage({ id: 'select.search', defaultMessage: 'Search' }),
        }}
      >
        <TenantOptions options={filteredTenantOptions} selectedTenant={selectedTenant} onChange={onChange} />
      </Select>
    </div>
  );
}
